import Grid from '@react-css/grid';
import { useEffect, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import EditShopifySettings from 'containers/SettingsPage/DropshippingTab/IntegrationsSettings/Shopify/EditShopifySettings';
import EditStoreNameButton from 'containers/SettingsPage/DropshippingTab/IntegrationsSettings/Shopify/EditStoreNameButton';
import ShopifyLocationsModal from 'containers/SettingsPage/DropshippingTab/IntegrationsSettings/Shopify/ShopifyLocationsModal';
import useAlertQueue from 'hooks/useAlertQueue';
import { useCanEditStoreName } from 'hooks/useCanEditStoreName';
import { isEmpty } from 'lodash';
import { selectCompany, selectIsSeller } from 'store/selectors/me/company';
import { selectIsSuperUser } from 'store/selectors/me/user';
import { updateShopifyAuth, updateShopifySettings } from 'store/slices/company';
import { useAppDispatch } from 'store';
import { SettingsDescription, SettingsSectionTitle } from 'containers/SettingsPage/SettingsLayout';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import { getShopifyAuthUrl, getShopifyStore } from 'utils/platform';
import EditShareCustomerSettings from './EditShareCustomerSettings';

interface ConnectFormProps {
  updatedStore: string;
  updatedToken: string;
  productCollectionId?: string;
}

const Connect = () => {
  const dispatch = useAppDispatch();
  const { addSuccessAlert, addErrorAlert } = useAlertQueue();

  const company = useSelector(selectCompany);
  const isSuperUser = useSelector(selectIsSuperUser);
  const isSeller = useSelector(selectIsSeller);
  const store = getShopifyStore(company);
  const currentProductCollectionId = company?.shopify?.productCollectionId;

  const [shopifyAuthUrl, setShopifyAuthUrl] = useState<string>(getShopifyAuthUrl(store));
  const [overrideLongPress, setOverrideLongPress] = useState<boolean | undefined>(undefined);
  const [overrideEnabled, setOverrideEnabled] = useState<boolean | undefined>(undefined);
  const [preventOpenTab, setPreventOpenTab] = useState<boolean>(false);
  const [isEditingAccessToken, setIsEditingAccessToken] = useState<boolean>(false);
  const [isEditingStoreURL, setIsEditingStoreURL] = useState<boolean>(false);
  const [isEditingProductCollectionId, setIsEditingProductCollectionId] = useState<boolean>(false);
  const [isLocationsModalOpen, setIsLocationsModalOpen] = useState<boolean>(false);

  const [isStoreNameEditable] = useCanEditStoreName();

  const isShopifyConnected = company?.productPlatformActive;
  const isMissingUrl = !isStoreNameEditable && !store;

  const onCancel = () => {
    setIsEditingAccessToken(false);
    setIsEditingStoreURL(false);
    setIsEditingProductCollectionId(false);
    setOverrideEnabled(false);
  };

  const buildUpdates = ({ updatedStore, updatedToken, productCollectionId }: ConnectFormProps) => {
    if (!isEmpty(updatedStore) && !isEmpty(updatedToken)) {
      return {
        shopifyAuth: {
          platform: 'shopify',
          store: updatedStore,
          accessToken: updatedToken,
        },
      };
    }
    if (!isEmpty(updatedStore)) {
      return {
        shopifyAuth: {
          platform: 'shopify',
          store: updatedStore,
        },
      };
    }
    if (!isEmpty(updatedToken)) {
      return {
        shopifyAuth: {
          platform: 'shopify',
          accessToken: updatedToken,
        },
      };
    }
    if (!isEmpty(productCollectionId)) {
      return {
        shopifyAuth: {
          platform: 'shopify',
          productCollectionId,
        },
      };
    }
    return {
      shopifyAuth: {
        platform: 'shopify',
      },
    };
  };

  const onSubmit = ({ updatedStore, updatedToken, productCollectionId }: ConnectFormProps) => {
    // Use updateShopifySettings for productCollectionId updates
    if (!updatedStore && !updatedToken && productCollectionId !== undefined) {
      return dispatch(updateShopifySettings({ productCollectionId })).then(
        (action: { error?: { message: string } }) => {
          if (action.error) {
            addErrorAlert('Something went wrong', action.error.message);
            return;
          }
          addSuccessAlert('Success', 'Updated Shopify Collection ID');
          setIsEditingProductCollectionId(false);
        }
      );
    }

    // Otherwise use updateShopifyAuth for other updates
    return dispatch(
      updateShopifyAuth(buildUpdates({ updatedStore, updatedToken, productCollectionId }))
    ).then((action: { error?: { message: string } }) => {
      if (action.error) {
        addErrorAlert('Something went wrong', action.error.message);
        return;
      }
      addSuccessAlert('Success', 'Updated Shopify Settings');
      if (updatedStore) {
        setShopifyAuthUrl(getShopifyAuthUrl(updatedStore));
      }
      setIsEditingAccessToken(false);
      setOverrideEnabled(false);
      setIsEditingStoreURL(false);
      setIsEditingProductCollectionId(false);
    });
  };

  const openShopifyAuthTab = () => {
    if (!preventOpenTab && overrideLongPress !== undefined) {
      window.open(shopifyAuthUrl, '_blank', 'noopener,noreferrer');
    }
    setPreventOpenTab(false);
  };

  useEffect(() => {
    // This effect is used to track how long a user has been
    // pressing down on the Connect button. This is then used to unlock
    // API Access Token override if the button is preseed for 5 seconds.
    let overrideTimer: NodeJS.Timeout | undefined;
    if (overrideLongPress) {
      // If it's a press down then we start a timer
      overrideTimer = setTimeout(() => {
        setOverrideEnabled(true);
        // we prevent the default of the button in this case since the
        // intent was to unlock the hidden button and not to open a new tab
        setPreventOpenTab(true);
      }, 5000);
    } else {
      // If the press is released we clear the timer. This essentially means that
      // unless it's interrupted the above timer function will trigger.
      clearTimeout(overrideTimer);
    }
    return () => {
      clearTimeout(overrideTimer);
    };
  }, [overrideLongPress, shopifyAuthUrl, preventOpenTab]);

  // Reset all the state variables on destroy
  useEffect(
    () => () => {
      setOverrideLongPress(undefined);
      setOverrideEnabled(undefined);
      setPreventOpenTab(false);
    },
    []
  );

  return (
    <>
      <hr style={{ margin: '24px 0' }} />
      <SettingsSectionTitle>
        Connect your Shopify Store
        {isShopifyConnected && (
          <Badge variant="success" pill className="ml-3 mt-n1">
            Connected
          </Badge>
        )}
      </SettingsSectionTitle>

      {!isShopifyConnected && (
        <SettingsDescription>
          Please connect the Modern Dropship Shopify app to your store.
        </SettingsDescription>
      )}

      {isMissingUrl ? (
        <SettingsDescription>
          <strong>Your Shopify Store URL is missing! Please contact support.</strong>
        </SettingsDescription>
      ) : (
        <SettingsDescription>
          Store: <strong>{`https://${store}.myshopify.com`}</strong>
        </SettingsDescription>
      )}

      <Grid gap="8px" alignItemsStart justifyContentStart columns="1fr 1fr">
        <SecondaryButton
          size="small"
          onClick={() => openShopifyAuthTab()}
          onMouseDown={() => setOverrideLongPress(true)}
          onMouseUp={() => setOverrideLongPress(true)}
          onMouseLeave={() => setOverrideLongPress(false)}
          onTouchStart={() => setOverrideLongPress(true)}
          onTouchEnd={() => setOverrideLongPress(false)}
          disabled={!store || isEditingStoreURL || isEditingAccessToken}
          kind={overrideEnabled ? 'neutral' : 'action'}
        >
          {isShopifyConnected ? 'Re-Connect Shopify' : 'Connect Shopify'}
        </SecondaryButton>

        {overrideEnabled && (
          <SecondaryButton
            kind="destructive"
            size="small"
            onClick={() => setIsEditingAccessToken(true)}
          >
            Override API Token
          </SecondaryButton>
        )}
        {!overrideEnabled && !isEditingStoreURL && (
          <EditStoreNameButton onClick={() => setIsEditingStoreURL(true)} />
        )}
        {isSuperUser && !isEditingProductCollectionId && (
          <SecondaryButton
            kind="neutral"
            size="small"
            onClick={() => setIsEditingProductCollectionId(true)}
          >
            Edit Collection ID
          </SecondaryButton>
        )}
        {isShopifyConnected && isSeller && (
          <SecondaryButton
            kind="neutral"
            size="small"
            onClick={() => setIsLocationsModalOpen(true)}
          >
            Manage Shopify Locations
          </SecondaryButton>
        )}
      </Grid>

      {(isEditingAccessToken || isEditingStoreURL || isEditingProductCollectionId) && (
        <EditShopifySettings
          isEditable={isStoreNameEditable}
          isEditingAccessToken={isEditingAccessToken}
          onSubmit={onSubmit}
          onCancel={onCancel}
          store={store}
          productCollectionId={currentProductCollectionId}
        />
      )}

      <hr className="my-4" />
      <EditShareCustomerSettings />

      <ShopifyLocationsModal
        show={isLocationsModalOpen}
        onHide={() => setIsLocationsModalOpen(false)}
      />
    </>
  );
};

export default Connect;
